<template>
  <div>
    <app-spinner v-if="!page_loaded"/>
    <template v-else>
      <h1 class="title">{{ item_data.name }}</h1>
      <div class="info-cards">
        <div class="info-cards__card">
          <button class="info-cards__control" @click="onShowEditModal">
            <img src="@/assets/img/edit-table-icon.svg" alt="">
          </button>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Section:</span> <span>{{ item_data.section.name }}</span>
            </div>
          </div>
        </div>
      </div>
      <template v-if="item_data?.item_type?.code === 'variants'">
        <app-cells>
          <app-button @click.native="onShowModal('variants', null)">Create</app-button>
        </app-cells>
        <v-client-table
          :data="table_variants.items"
          :columns="table_variants.columns"
          :options="table_variants.options"
          class="table-default"
        >
          <template #h__public_name><div class="table-default__left">Name</div></template>
          <template #public_name="{ row }"><div class="table-default__left">{{ row.public_name }}</div></template>
          <template #stock="{ row }">{{ row.stock || '-' }}</template>
          <template #weight="{ row }">{{ row.weight || '-' }}</template>
          <template #conv="{ row }">{{ row.conversion_coefficient || '-' }}</template>
          <template #controls="{ row }">
            <div class="table-default__controls">
              <button @click="onShowModal('variants', row)" type="button">
                <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
              </button>
            </div>
          </template>
        </v-client-table>
      </template>
      <template v-if="item_data?.item_type?.code === 'trackable'">
        <app-cells>
          <app-button @click.native="onShowModal('trackable', null)">Create</app-button>
        </app-cells>
        <v-client-table
          :data="table_trackable.items"
          :columns="table_trackable.columns"
          :options="table_trackable.options"
          class="table-default"
        >
          <template #h__public_name><div class="table-default__left">Name</div></template>
          <template #public_name="{ row }"><div class="table-default__left">{{ row.public_name }}</div></template>
          <template #number="{ row }">{{ row.number || '-' }}</template>
          <template #status="{ row }">
            <span class="status-tag" :style="{backgroundColor: row.status.color}">{{ row.status.name }}</span>
          </template>
          <template #brand="{ row }">{{ row.brand.name || '-' }}</template>
          <template #reserved="{ row }">
            <span
              class="status-tag" :style="{backgroundColor: row.reserved ? '#77b017' : '#e85c20'}"
            >
              {{ row.reserved ? 'Yes' : 'No' }}
            </span>
          </template>
          <template #controls="{ row }">
            <div class="table-default__controls">
              <button @click="onShowModal('trackable', row)" type="button">
                <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
              </button>
            </div>
          </template>
        </v-client-table>
      </template>
      <modal
        name="item_modal"
        :width="400"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        draggable=".popup__title"
        class="modal-not-overflow"
      >
        <div class="popup">
          <button @click="$modal.hide('item_modal')" class="popup__close" type="button"></button>
          <div class="popup__title">Update Item</div>
          <div class="popup__body">
            <form @submit.prevent="onCheckForm('form', 'sendForm')">
              <app-form-group label="Section" required>
                <app-select
                  @input="onChangeSection"
                  v-model="form.section"
                  :reduce="item => item.id"
                  :options="section_options"
                  placeholder="Section"
                  :error="$v.form.section.$error"
                />
                <template #error>
                  <div v-if="$v.form.section.$dirty && !$v.form.section.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group label="Category">
                <app-select
                  v-model="form.category"
                  :reduce="item => item.id"
                  :options="categories_options"
                  :readonly="!categories_options.length"
                  placeholder="Category"
                />
              </app-form-group>
              <app-form-group label="Measurement" required>
                <app-select
                  v-model="form.measurement"
                  :reduce="item => item.id"
                  :options="measurements_options"
                  placeholder="Measurement"
                  :error="$v.form.measurement.$error"
                />
                <template #error>
                  <div v-if="$v.form.measurement.$dirty && !$v.form.measurement.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group label="Name" required>
                <app-input
                  v-model="form.name"
                  placeholder="Name"
                  :error="$v.form.name.$error"
                />
                <template #error>
                  <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group label="Weight">
                <app-fin-number
                  v-model="form.weight"
                  placeholder="Weight"
                />
              </app-form-group>
              <app-cells position="center" :indent="false">
                <app-button ref="submit" type="submit">Save</app-button>
              </app-cells>
            </form>
          </div>
        </div>
      </modal>
      <modal
        name="type_modal"
        :width="400"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        draggable=".popup__title"
        class="modal-not-overflow"
      >
        <div class="popup">
          <button @click="$modal.hide('type_modal')" class="popup__close" type="button"></button>
          <div class="popup__title">{{ item_to_action ? 'Update' : 'Create' }}</div>
          <div class="popup__body">
            <form @submit.prevent="onCheckForm('form_type', 'sendFormType')">
              <template v-if="item_type === 'variants'">
                <app-form-group label="Name" required>
                  <app-input
                    v-model="form_type.name"
                    placeholder="Name"
                    :error="$v.form_type.name.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_type.name.$dirty && !$v.form_type.name.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Weight">
                  <app-fin-number
                    v-model="form_type.weight"
                    placeholder="Weight"
                  />
                </app-form-group>
                <template v-if="!item_to_action">
                  <app-form-group  label="Stock">
                    <app-fin-number
                      v-model="form_type.stock"
                      placeholder="Stock"
                    />
                  </app-form-group>
                  <app-form-group  label="Conversion rate">
                    <app-fin-number
                      v-model="form_type.conversion_coefficient"
                      :decimal-part-count="4"
                      placeholder="Conversion rate"
                    />
                  </app-form-group>
                </template>
              </template>
              <template v-else>
                <app-form-group label="Number" required label-for="type_number">
                  <app-input
                    v-model="form_type.number"
                    id="type_number"
                    placeholder="Number"
                    :error="$v.form_type.number.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_type.number.$dirty && !$v.form_type.number.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Status" required>
                  <app-select
                    v-model="form_type.status"
                    :reduce="item => item.code"
                    :options="status_options"
                    placeholder="Status"
                    :error="$v.form_type.status.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_type.status.$dirty && !$v.form_type.status.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Brand">
                  <app-select
                    v-model="form_type.brand"
                    :reduce="item => item.id"
                    :options="brand_options"
                    placeholder="Brand"
                  />
                </app-form-group>
                <app-form-group label="Reserved">
                  <app-switcher
                    v-model="form_type.reserved"
                  />
                </app-form-group>
              </template>
              <app-cells position="center" :indent="false">
                <app-button ref="submit_type" type="submit">{{ item_to_action ? 'Save' : 'Create' }}</app-button>
              </app-cells>
            </form>
          </div>
        </div>
      </modal>
    </template>
  </div>
</template>

<script>
import {
  getStorageDictsCategories,
  getStorageItemsDetail,
  patchStorageItems,
  getStorageDictsSections,
  getStorageDictsMeasurements,
  getStorageItemsTypeVariants,
  postStorageItemsTypeVariants,
  patchStorageItemsTypeVariants,
  getStorageItemsTypeTrackable,
  postStorageItemsTypeTrackable,
  patchStorageItemsTypeTrackable,
  getStorageDictsStatuses,
  getStorageDictsBrands
} from '@/http'
import { required, requiredIf } from 'vuelidate/lib/validators'

export default {
  name: 'StorageItemsDetail',
  data() {
    return {
      page_loaded: false,
      item_data: {},
      section_options: [],
      categories_options: [],
      measurements_options: [],
      status_options: [],
      brand_options: [],
      form: {},
      form_type: {},
      item_to_action: null,
      item_type: null,
      table_variants: {
        items: [],
        columns: [
          'public_name',
          'stock',
          'weight',
          'conv',
          'controls',
        ],
        options: {
          headings: {
            'public_name': 'Name',
            'stock': 'Stock',
            'weight': 'Weight',
            'conv': 'Conv. rate',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_trackable: {
        items: [],
        columns: [
          'public_name',
          'number',
          'status',
          'brand',
          'reserved',
          'controls',
        ],
        options: {
          headings: {
            'public_name': 'Name',
            'number': 'Number',
            'status': 'Status',
            'brand': 'Brand',
            'reserved': 'Reserved',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
    }
  },
  validations: {
    form: {
      section: {required},
      measurement: {required},
      name: {required},
    },
    form_type: {
      name: {
        required: requiredIf(function() {
          return this.item_type === 'variants'
        })
      },
      number: {
        required: requiredIf(function() {
          return this.item_type === 'trackable'
        })
      },
      status: {
        required: requiredIf(function() {
          return this.item_type === 'trackable'
        })
      },
    }
  },
  created() {
    Promise.all([
      getStorageDictsSections(),
      getStorageDictsMeasurements(),
      getStorageDictsStatuses(),
      getStorageDictsBrands(),
      this.fetchItemData(),
    ])
      .then(response => {
        this.section_options = response[0].data
        this.measurements_options = response[1].data
        this.status_options = response[2].data
        this.brand_options = response[3].data
        if (this.item_data.item_type?.code === 'variants') {
          this.fetchItemsVariants()
        }
        if (this.item_data.item_type?.code === 'trackable') {
          this.fetchItemsTrackable()
        }
        this.page_loaded = true
      })
  },
  methods: {
    async fetchItemData() {
      const response = await getStorageItemsDetail(this.$route.params.id)
      this.item_data = response.data
    },
    fetchItemsVariants() {
      getStorageItemsTypeVariants(this.$route.params.id)
        .then(response => {
          this.table_variants.items = response.data
        })
    },
    fetchItemsTrackable() {
      getStorageItemsTypeTrackable(this.$route.params.id)
        .then(response => {
          this.table_trackable.items = response.data
        })
    },
    onCheckForm(form, action) {
      this.$v[form].$touch()
      if (this.$v[form].$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this[action]()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      patchStorageItems(this.$route.params.id, this.form)
        .then(() => {
          this.fetchItemData()
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
          this.$modal.hide('item_modal')
        })
        .catch(() => {
          this.$refs.submit.preload = false
        })
    },
    sendFormType() {
      this.$refs.submit_type.preload = true
      if (this.item_type === 'variants') {
        if (!this.item_to_action) {
          postStorageItemsTypeVariants(this.$route.params.id, this.form_type)
            .then(() => {
              this.fetchItemsVariants()
              this.$notify({
                type: 'success',
                title: 'Data saved',
              })
              this.$modal.hide('type_modal')
            })
            .catch(() => {
              this.$refs.submit_type.preload = false
            })
        } else {
          patchStorageItemsTypeVariants(this.$route.params.id, this.item_to_action, this.form_type)
            .then(() => {
              this.fetchItemsVariants()
              this.$notify({
                type: 'success',
                title: 'Data saved',
              })
              this.$modal.hide('type_modal')
            })
            .catch(() => {
              this.$refs.submit_type.preload = false
            })
        }
      } else {
        if (!this.item_to_action) {
          postStorageItemsTypeTrackable(this.$route.params.id, this.form_type)
            .then(() => {
              this.fetchItemsTrackable()
              this.$notify({
                type: 'success',
                title: 'Data saved',
              })
              this.$modal.hide('type_modal')
            })
            .catch(() => {
              this.$refs.submit_type.preload = false
            })
        } else {
          patchStorageItemsTypeTrackable(this.$route.params.id, this.item_to_action, this.form_type)
            .then(() => {
              this.fetchItemsTrackable()
              this.$notify({
                type: 'success',
                title: 'Data saved',
              })
              this.$modal.hide('type_modal')
            })
            .catch(() => {
              this.$refs.submit_type.preload = false
            })
        }
      }
    },
    onShowModal(type, row) {
      this.item_to_action = row?.id
      this.item_type = type
      if (!row) {
        this.form_type = {}
      } else {
        if (type === 'variants') {
          this.form_type = {
            stock: row.stock,
            name: row.name,
            weight: row.weight,
          }
        } else {
          this.form_type = {
            brand: row.brand?.id,
            status: row.status.code,
            number: row.number,
            reserved: row.reserved,
          }
        }
      }
      this.$v.form_type.$reset()
      this.$modal.show('type_modal')
    },
    onShowEditModal() {
      getStorageDictsCategories({section: this.item_data.section.id})
        .then(response => {
          this.categories_options = response.data
          this.form = {
            section: this.item_data.section.id,
            measurement: this.item_data.measurement.id,
            category: this.item_data.category?.id,
            name: this.item_data.name,
            weight: this.item_data.weight,
          }
        })
      this.$v.form.$reset()
      this.$modal.show('item_modal')
    },
    onChangeSection(e) {
      this.form.category = null
      this.categories_options = []
      if (e) {
        getStorageDictsCategories({section: e})
          .then(response => {
            this.categories_options = response.data
          })
      }
    },
  }
}
</script>

